import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { getBranchPointSetting } from '../../api/member-point';
import { POINT_EXPIRE_SOURCE, POINT_PERIOD_UNIT, computePointExpireDate } from '../../constants/point';

const TEAL = '#0F7F85';
const TEAL_LIGHT = '#E7F6F7';
const TEAL_BORDER = '#BFE3E5';

// 자주 쓰는 기간 프리셋 — 값 0 은 무기한
const PRESETS = [
  { label: '7일', value: 7, unit: POINT_PERIOD_UNIT.DAY },
  { label: '1개월', value: 1, unit: POINT_PERIOD_UNIT.MONTH },
  { label: '3개월', value: 3, unit: POINT_PERIOD_UNIT.MONTH },
  { label: '6개월', value: 6, unit: POINT_PERIOD_UNIT.MONTH },
  { label: '1년', value: 12, unit: POINT_PERIOD_UNIT.MONTH },
  { label: '무기한', value: 0, unit: null },
];

const cardSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px 20px',
  borderRadius: '10px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#FAFAFA',
  marginBottom: '16px',
};

const inputBase = {
  height: 40,
  border: '1px solid #D9DDE3',
  borderRadius: 8,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  outline: 'none',
  background: '#fff',
  color: '#111',
};

const chipSx = active => ({
  height: 32,
  px: '12px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  border: `1px solid ${active ? TEAL : '#D9DDE3'}`,
  background: active ? TEAL : '#fff',
  color: active ? '#fff' : '#4A4F57',
  userSelect: 'none',
});

const unitSx = active => ({
  height: 32,
  px: '12px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '6px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  background: active ? '#fff' : 'transparent',
  color: active ? TEAL : '#8A8F98',
  boxShadow: active ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
});

const linkSx = {
  fontSize: 13,
  fontWeight: 700,
  color: TEAL,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
};

const describePeriod = (value, unit, t) => `${value}${unit === POINT_PERIOD_UNIT.DAY ? t('일') : t('개월')}`;

/**
 * 수기·이벤트 지급 유효기간 카드.
 *
 * - 기본은 지점 설정(수기·이벤트 규칙)을 큰 글씨로 보여주고, '이번 건만 변경' 을 누르면
 *   프리셋 칩(7일·1개월·…·무기한) + 직접 입력(값·단위·만료일)이 펼쳐진다.
 * - onChange({ expireDate, noExpire }) — 접힌 상태면 둘 다 비움(=지점 규칙대로), 펼쳐서 정했으면
 *   만료일 또는 noExpire=true(규칙이 켜져 있어도 이번 건은 무기한).
 */
const PointExpireField = ({ branchId, token, onChange, resetKey }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [periodValue, setPeriodValue] = useState('');
  const [periodUnit, setPeriodUnit] = useState(POINT_PERIOD_UNIT.MONTH);
  const [expireDate, setExpireDate] = useState('');

  const { data: setting } = useQuery({
    queryKey: ['branchPointSetting', branchId],
    queryFn: () => getBranchPointSetting(branchId, token),
    enabled: !!branchId && !!token,
  });
  // 수기·이벤트(ADMIN) 유형의 지점 규칙
  const adminRule = (setting?.expireRules || []).find(rule => rule.source === POINT_EXPIRE_SOURCE.ADMIN);
  const ruleOn = adminRule?.enabledYn === 'Y' && !!adminRule.periodValue;
  const ruleUnit = adminRule?.periodUnit || POINT_PERIOD_UNIT.MONTH;
  const ruleLabel = t('지점 설정 기본값');
  const ruleDate = ruleOn ? computePointExpireDate(adminRule.periodValue, ruleUnit) : '';

  const resetToRule = () => {
    setPeriodValue(ruleOn ? String(adminRule.periodValue) : '');
    setPeriodUnit(ruleUnit);
    setExpireDate(ruleDate);
  };

  // 모달을 열 때마다(resetKey) 지점 규칙으로 되돌린다. 설정 조회가 늦게 끝나 값이 바뀌어도
  // 이미 '이번 건만 변경' 중이면 사용자가 고른 값을 덮어쓰지 않는다.
  useEffect(() => {
    if (editing) return;
    resetToRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleOn, adminRule?.periodValue, ruleUnit]);
  useEffect(() => {
    setEditing(false);
    resetToRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // 접힌 상태 = 규칙대로(둘 다 비움). 펼쳐서 정했으면 만료일, 비웠으면 이번 건 무기한.
  useEffect(() => {
    if (!editing) onChange({ expireDate: null, noExpire: false });
    else onChange({ expireDate: expireDate || null, noExpire: !expireDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, expireDate]);

  const applyPeriod = (value, unit) => {
    setPeriodValue(value);
    setPeriodUnit(unit);
    setExpireDate(value ? computePointExpireDate(Number(value), unit) : '');
  };

  const isPresetActive = preset =>
    preset.value === 0 ? periodValue === '' && expireDate === '' : String(preset.value) === periodValue && preset.unit === periodUnit;

  const today = dayjs().format('YYYY-MM-DD');

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <Box sx={{ fontSize: 13, fontWeight: 600, color: '#3D3D3D' }}>{t('유효기간')}</Box>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            px: '8px',
            height: 20,
            display: 'flex',
            alignItems: 'center',
            borderRadius: '10px',
            background: editing ? '#FFF1EF' : TEAL_LIGHT,
            color: editing ? '#F1685A' : TEAL,
          }}>
          {editing ? t('이번 건만 변경') : ruleLabel}
        </Box>
      </Box>

      {!editing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
            <Box sx={{ fontSize: 20, fontWeight: 700, color: '#111' }}>
              {ruleOn ? describePeriod(adminRule.periodValue, ruleUnit, t) : t('무기한')}
            </Box>
            {ruleOn && (
              <Box sx={{ fontSize: 13, color: '#6E747E' }}>
                {ruleDate} {t('까지 사용 가능')}
              </Box>
            )}
          </Box>
          <Box onClick={() => setEditing(true)} sx={linkSx}>
            {t('이번 건만 변경')}
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {PRESETS.map(preset => (
              <Box key={preset.label} sx={chipSx(isPresetActive(preset))} onClick={() => applyPeriod(preset.value ? String(preset.value) : '', preset.unit || periodUnit)}>
                {t(preset.label)}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Box sx={{ fontSize: 13, color: '#6E747E', whiteSpace: 'nowrap' }}>{t('직접 입력')}</Box>
            <input
              style={{ ...inputBase, width: 72, textAlign: 'right', fontWeight: 700 }}
              value={periodValue}
              onChange={e => applyPeriod(e.target.value.replace(/[^0-9]/g, ''), periodUnit)}
              placeholder="0"
            />
            <Box sx={{ display: 'flex', background: '#EDEFF2', borderRadius: '8px', p: '3px', gap: '2px', flex: 'none' }}>
              {[
                { value: POINT_PERIOD_UNIT.DAY, label: t('일') },
                { value: POINT_PERIOD_UNIT.MONTH, label: t('개월') },
              ].map(option => (
                <Box key={option.value} sx={unitSx(periodUnit === option.value)} onClick={() => applyPeriod(periodValue, option.value)}>
                  {option.label}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Box sx={{ fontSize: 13, color: '#6E747E', whiteSpace: 'nowrap' }}>{t('만료일 직접 지정')}</Box>
            <input
              type="date"
              style={{ ...inputBase, width: 160 }}
              value={expireDate}
              min={today}
              onChange={e => {
                // 날짜를 직접 고치면 기간 입력은 더 이상 근거가 아니므로 비운다
                setPeriodValue('');
                setExpireDate(e.target.value);
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
              padding: '10px 14px',
              borderRadius: '8px',
              border: `1px solid ${TEAL_BORDER}`,
              background: '#F3FAFB',
            }}>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: expireDate ? TEAL : '#4A4F57' }}>
              {expireDate ? `${t('이번 지급분은')} ${expireDate} ${t('까지 사용 가능')}` : t('이번 지급분은 무기한')}
            </Box>
            <Box
              onClick={() => {
                resetToRule();
                setEditing(false);
              }}
              sx={{ ...linkSx, color: '#6E747E' }}>
              {t('기본값으로')}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PointExpireField;
